import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomValidators } from 'angular-custom-validators';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  setDoc,
} from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { LogType } from 'src/app/enums';
import { lastValueFrom } from 'rxjs';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { getObjectDifferences } from 'ts-object-difference';
import { ucFirst } from 'src/app/globals';
import {
  Characteristic,
  PhoneNumber,
  StandardLocation,
  Rate,
  Vehicle,
  VehicleGroup,
  RidePreparationLog,
} from 'src/app/interfaces';
import { AddressValidationService } from 'src/app/services/address-validation.service';
import { LocalCustomValidators } from 'src/app/validators/local-custom-validators';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { OrsAddressAutocompleteComponent } from '../../../components/ors-address-autocomplete/ors-address-autocomplete.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ColorPickerDirective } from 'ngx-color-picker';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgStyle, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';

export interface DialogData {
  item?: Vehicle | VehicleGroup | Rate | Characteristic | StandardLocation | PhoneNumber;
  itemLabel:
    | 'voertuig'
    | 'voertuiggroep'
    | 'tarief'
    | 'kenmerk'
    | 'standaardlocatie'
    | 'telefoonnummer';
  fields: Field[];
  optionalData?: any;
  beforeSave?: (saveObj: Record<string, unknown>) => Promise<boolean>;
}

/**
 * Adjacent fields with the same row value render together; fields without a row value render individually in the normal sequential path.
 * Fields with the same row value separated by other fields render separately to preserve order.
 */
export interface Field {
  key: string;
  type: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  group?: any[];
  min?: number;
  decimal?: number;
  showWhen?: { field: string; value: unknown };
  searchable?: boolean;
  row?: string;
}

@Component({
  selector: 'app-edit-ride-preparation-item-dialog',
  templateUrl: './edit-ride-preparation-item-dialog.component.html',
  styleUrl: './edit-ride-preparation-item-dialog.component.scss',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    OrsAddressAutocompleteComponent,
    MatSlideToggle,
    ColorPickerDirective,
    MatIconButton,
    MatSuffix,
    MatIcon,
    NgStyle,
    MatCheckbox,
    NgClass,
    NgTemplateOutlet,
    MatTooltip,
    MatSelect,
    MatOption,
    MatSelectSearchComponent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class EditRidePreparationItemDialogComponent implements OnInit {
  form: FormGroup = this.fb.group({});
  organisationId = dataStore.selectedOrganisation.id;
  loading = signal(true);
  saving = signal(false);
  colorPickers: any = {};
  selectSearchControls: Record<string, FormControl> = {};
  filteredSelectOptions: Record<string, any[]> = {};
  ucFirst = ucFirst;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EditRidePreparationItemDialogComponent>,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    @Inject(AddressValidationService) private addressValidationService: AddressValidationService,
  ) {}

  ngOnInit(): void {
    console.log('EditRidePreparationItemDialogComponent data', this.data);
    // remove null and invalid fields from fields array
    this.data.fields = this.data.fields.filter(this.isValidField);

    if (this.data?.item?.['calendarStyle']?.['sortingNumber']) {
      this.data.item['sortingNumber'] = this.data.item['calendarStyle']['sortingNumber'];
    }

    for (const field of this.data.fields) {
      this.createFormControl(field);
    }

    if (this.data?.item) {
      this.form.patchValue(this.data.item);
      if (this.data.item?.['calendarStyle']) {
        this.form.patchValue(this.data.item['calendarStyle']);
      }
    }
    if (this.form.get('vehicleType') && !this.form.get('vehicleType')?.value) {
      this.form.get('vehicleType')?.setValue('organisation');
    }
    const vehicleTypeControl = this.form.get('vehicleType');
    vehicleTypeControl?.valueChanges.subscribe((vehicleType) => {
      this.updateVehicleAssignmentControls(vehicleType);
    });
    this.updateVehicleAssignmentControls(vehicleTypeControl?.value);
    this.loading.set(false);
  }

  isFieldVisible(field: Field): boolean {
    return !field.showWhen || this.form.get(field.showWhen.field)?.value === field.showWhen.value;
  }

  hasFieldRows(): boolean {
    return this.data.fields.some((field) => !!field.row);
  }

  getFieldRows(): { key: string; fields: Field[] }[] {
    const rows = new Map<string, Field[]>();
    for (const field of this.data.fields) {
      if (!field.row || !this.isFieldVisible(field)) {
        continue;
      }
      const rowFields = rows.get(field.row) ?? [];
      rowFields.push(field);
      rows.set(field.row, rowFields);
    }
    return Array.from(rows, ([key, fields]) => ({ key, fields }));
  }

  getFieldRenderGroups(): { row?: string; fields: Field[] }[] {
    const groups: { row?: string; fields: Field[] }[] = [];
    for (const field of this.data.fields) {
      if (!this.isFieldVisible(field)) {
        continue;
      }
      const previousGroup = groups.at(-1);
      if (field.row && previousGroup?.row === field.row) {
        previousGroup.fields.push(field);
      } else {
        groups.push({ row: field.row, fields: [field] });
      }
    }
    return groups;
  }

  private updateVehicleAssignmentControls(vehicleType: string): void {
    const driverControl = this.form.get('driverId');
    const coDriverControl = this.form.get('coDriverId');
    if (vehicleType === 'private') {
      driverControl?.setValidators(Validators.required);
      coDriverControl?.clearValidators();
    } else {
      driverControl?.clearValidators();
      coDriverControl?.clearValidators();
      driverControl?.setValue(null, { emitEvent: false });
      coDriverControl?.setValue(null, { emitEvent: false });
    }
    driverControl?.updateValueAndValidity({ emitEvent: false });
    coDriverControl?.updateValueAndValidity({ emitEvent: false });
  }

  isValidField(obj: any): obj is Field {
    return (
      obj &&
      typeof obj.key === 'string' &&
      typeof obj.type === 'string' &&
      typeof obj.label === 'string' &&
      (!obj.required || typeof obj.required === 'boolean')
    );
  }

  createFormControl(field: Field) {
    const validators = [];
    switch (field.type) {
      case 'string':
        this.form.addControl(field.key, this.fb.control(''));
        break;
      case 'addressAutocomplete':
        this.form.addControl(field.key, this.fb.control(''));
        break;
      case 'number':
        this.form.addControl(field.key, this.fb.control(0));
        break;
      case 'numberInput':
        validators.push(
          CustomValidators.numberInput(
            field.min !== undefined ? true : false,
            field.decimal ? true : false,
            field.decimal !== undefined && field.decimal !== null ? field.decimal : 0,
          ),
        );
        this.form.addControl(field.key, this.fb.control(null));
        break;
      case 'order':
        validators.push(
          CustomValidators.numberInput(field.min !== undefined ? true : false, false, 0),
        );
        validators.push(
          LocalCustomValidators.orderValidator(
            this.data.optionalData.orderNumbersArray,
            this.data?.item ? true : false,
            this.data?.item ? this.data.item[field.key] : null,
          ),
        );
        this.form.addControl(field.key, this.fb.control(null));
        break;
      case 'phone':
        validators.push(CustomValidators.phone);
        this.form.addControl(field.key, this.fb.control(null));
        break;
      case 'toggle':
        this.form.addControl(field.key, this.fb.control(false));
        break;
      case 'select':
        this.form.addControl(field.key, this.fb.control(null));
        this.filteredSelectOptions[field.key] = [...(field.group ?? [])];
        if (field.searchable) {
          const searchControl = new FormControl('');
          this.selectSearchControls[field.key] = searchControl;
          searchControl.valueChanges.subscribe((searchValue) => {
            this.filterSelectOptions(field, searchValue ?? '');
          });
        }
        break;
      case 'color':
        this.form.addControl(field.key, this.fb.control('#efefef'));
        this.colorPickers[field.key] = false;
        break;
      case 'checkboxGroup':
        this.form.addControl(field.key, this.fb.control([]));
        break;
      case 'iconGroup':
        this.form.addControl(field.key, this.fb.control([]));
        break;
      default:
        this.form.addControl(field.key, this.fb.control(null));
    }

    if (field.required) {
      validators.push(Validators.required);
    }
    if (field.min !== undefined) {
      validators.push(Validators.min(field.min));
    }
    if (field.disabled) {
      this.form.get(field.key)?.disable();
    }
    this.form.get(field.key)?.setValidators(validators);
  }

  filterSelectOptions(field: Field, searchValue: string): void {
    const normalizedSearch = searchValue.toLowerCase();
    const selectedValue = this.form.get(field.key)?.value;
    const selectedOption = (field.group ?? []).find((option) => option.id === selectedValue);
    const matchingOptions = (field.group ?? []).filter((option) =>
      option.name.toLowerCase().includes(normalizedSearch),
    );
    if (selectedOption && !matchingOptions.some((option) => option.id === selectedOption.id)) {
      matchingOptions.unshift(selectedOption);
    }
    this.filteredSelectOptions[field.key] = matchingOptions;
  }

  groupItemChange(event, fieldKey: string, value: any) {
    const currentArray = [...(this.form.get(fieldKey)?.value || [])];
    if (event.checked) {
      if (currentArray.indexOf(value) === -1) {
        currentArray.push(value);
      }
    } else {
      const index = currentArray.indexOf(value);
      if (index !== -1) {
        currentArray.splice(index, 1);
      }
    }
    this.form.get(fieldKey)?.setValue(currentArray);
  }

  iconItemChange(fieldKey: string, value: string) {
    const currentArray = [...(this.form.get(fieldKey)?.value || [])];
    const index = currentArray.indexOf(value);
    if (index !== -1) {
      currentArray.splice(index, 1);
    } else {
      currentArray.push(value);
    }
    this.form.get(fieldKey)?.setValue(currentArray);
  }

  checked(value: any, fieldKey: string): boolean {
    const currentArray = this.form.get(fieldKey)?.value || [];
    return currentArray.indexOf(value) !== -1;
  }

  async save() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving()) {
      return;
    }

    const saveObj = { ...this.form.value };
    saveObj.name = ucFirst(saveObj?.name ?? '');
    console.log('saveObj', saveObj);

    if (this.data.itemLabel === 'voertuig' && saveObj.vehicleType === 'private') {
      const driverOptions = this.data.fields.find((field) => field.key === 'driverId')?.group ?? [];
      const coDriverOptions =
        this.data.fields.find((field) => field.key === 'coDriverId')?.group ?? [];
      if (!driverOptions.some((option) => option.id === saveObj.driverId)) {
        this.snackBar.open('Selecteer een bestaande chauffeur voor dit privévoertuig.', 'X', {
          duration: 5000,
        });
        return;
      }
      if (
        saveObj.coDriverId &&
        !coDriverOptions.some((option) => option.id === saveObj.coDriverId)
      ) {
        this.snackBar.open('Selecteer een bestaande bijrijder voor dit privévoertuig.', 'X', {
          duration: 5000,
        });
        return;
      }
      if (saveObj.driverId === saveObj.coDriverId) {
        this.snackBar.open('De vaste chauffeur en bijrijder moeten verschillend zijn.', 'X', {
          duration: 5000,
        });
        return;
      }
    }

    this.saving.set(true);

    if (this.data.beforeSave) {
      try {
        const canSave = await this.data.beforeSave(saveObj);
        if (!canSave) {
          this.saving.set(false);
          return;
        }
      } catch (error) {
        console.error('Could not complete the pre-save validation', error);
        this.saving.set(false);
        this.snackBar.open('Er is een fout opgetreden. De wijzigingen zijn niet opgeslagen.', 'X', {
          duration: 5000,
        });
        return;
      }
    }

    const collectionPaths = {
      voertuig: `organisations/${this.organisationId}/vehicles`,
      voertuiggroep: `organisations/${this.organisationId}/vehicleGroups`,
      tarief: `organisations/${this.organisationId}/products`,
      kenmerk: `organisations/${this.organisationId}/characteristics`,
      standaardlocatie: `organisations/${this.organisationId}/addresses`,
      telefoonnummer: `organisations/${this.organisationId}/numbers`,
    };

    const collectionPath = collectionPaths[this.data.itemLabel];
    const docId = this.data?.item?.id ?? doc(collection(db, collectionPath)).id;
    const docRef = doc(db, collectionPath, docId);
    saveObj.id = docId;

    switch (this.data.itemLabel) {
      case 'voertuig':
        if (this.data?.item?.['active'] && !saveObj['active']) {
          saveObj['vehicleGroups'] = [];
        }
        await this.saveVehicle(saveObj, docRef);
        break;

      case 'voertuiggroep':
        await this.saveVehicleGroup(saveObj, docRef);
        break;

      case 'tarief':
        await this.saveRate(saveObj, docRef);
        break;

      case 'telefoonnummer':
        saveObj.order = Number(saveObj.order);
        await setDoc(docRef, saveObj, { merge: true });
        saveObj.id = docRef.id;
        await this.logChanges(saveObj);
        break;

      case 'standaardlocatie':
        const result = await this.saveStandardLocation(saveObj, docRef);
        if (!result) {
          this.saving.set(false);
          return;
        }
        break;
      case 'kenmerk':
      default:
        await setDoc(docRef, saveObj, { merge: true });
        saveObj.id = docRef.id;
        await this.logChanges(saveObj);
        break;
    }

    this.snackBar.open(
      this.data?.item
        ? `${ucFirst(this.data.itemLabel)} bijgewerkt.`
        : `${ucFirst(this.data.itemLabel)} toegevoegd.`,
      'X',
      { duration: 5000 },
    );
    this.dialogRef.close(saveObj);
  }

  async saveVehicle(saveObj, docRef) {
    saveObj.vehicleType = saveObj.vehicleType ?? 'organisation';
    if (saveObj.vehicleType !== 'private') {
      saveObj.driverId = deleteField();
      saveObj.coDriverId = deleteField();
    } else if (!this.form.contains('coDriverId') || !saveObj.coDriverId) {
      saveObj.coDriverId = deleteField();
    }
    const calendarStyleKeys = [
      'color',
      'backgroundColorEmpty',
      'backgroundColorFull',
      'backgroundColorNoDriver',
    ];
    saveObj.calendarStyle = {};
    calendarStyleKeys.forEach((key) => {
      if (saveObj[key]) {
        saveObj.calendarStyle[key] = saveObj[key];
        delete saveObj[key];
      }
    });
    if (saveObj.sortingNumber) {
      saveObj.calendarStyle.sortingNumber = Number(saveObj.sortingNumber);
      delete saveObj.sortingNumber;
    }
    if (saveObj.seats) {
      saveObj.seats = Number(saveObj.seats);
    }
    await setDoc(docRef, saveObj, { merge: true });
    saveObj.id = docRef.id;
    await this.logChanges(saveObj);
  }

  async saveVehicleGroup(saveObj, docRef) {
    const nonSelectedVehicles = this.data.optionalData.vehicles.filter(
      (vehicle) => !saveObj.vehicles.includes(vehicle.id),
    );
    const promises = [setDoc(docRef, saveObj, { merge: true })];
    saveObj.vehicles.forEach((vehicle) =>
      promises.push(
        setDoc(
          doc(db, `/organisations/${this.organisationId}/vehicles/${vehicle}`),
          { vehicleGroups: arrayUnion(docRef.id) },
          { merge: true },
        ),
      ),
    );
    nonSelectedVehicles.forEach((vehicle) =>
      promises.push(
        setDoc(
          doc(db, `/organisations/${this.organisationId}/vehicles/${vehicle.id}`),
          { vehicleGroups: arrayRemove(docRef.id) },
          { merge: true },
        ),
      ),
    );
    await Promise.allSettled(promises);
    saveObj.id = docRef.id;
    await this.logChanges(saveObj);
  }

  async saveRate(saveObj, docRef) {
    if (saveObj.cost !== undefined && saveObj.cost !== null) {
      saveObj.cost = Number(saveObj.cost);
    }
    const defaultRateId = this.data.optionalData?.rates.find((rate) => rate.default)?.id ?? null;
    if (saveObj.default && defaultRateId && defaultRateId !== docRef.id) {
      const result = await this.showWarningDialog('standardRate');
      if (result) {
        await setDoc(
          doc(db, docRef.parent.path, defaultRateId),
          { default: false },
          { merge: true },
        );
      } else {
        saveObj.default = false;
      }
    }
    await setDoc(docRef, saveObj, { merge: true });
    saveObj.id = docRef.id;
    await this.logChanges(saveObj);
  }

  async saveStandardLocation(saveObj, docRef): Promise<boolean> {
    const existingLocation = this.data.item as StandardLocation | undefined;
    const normalizedAddress = this.addressValidationService.normalizeAddressForComparison(
      saveObj.address,
    );
    const addressUnchanged =
      !!existingLocation &&
      this.addressValidationService.normalizeAddressForComparison(existingLocation.address) ===
        normalizedAddress;

    if (addressUnchanged && existingLocation?.coordinates) {
      const saveData = {
        ...saveObj,
        coordinates: existingLocation.coordinates,
        coordinatesFor: existingLocation.coordinatesFor ?? normalizedAddress,
      };
      await setDoc(docRef, saveData, { merge: true });
      saveObj.id = docRef.id;
      await this.logChanges(saveObj);
      return true;
    }

    const addressValidationResult = await this.addressValidationService.validateAddress(
      saveObj.address,
    );
    if (!addressValidationResult.isValid && !addressValidationResult.requiresConfirmation) {
      this.dialog.open(WarningDialog, {
        width: '600px',
        data: {
          dialogType: 'addressNotValid',
          optionalData: {
            deleteMode: false,
          },
        },
      });
      return false;
    }
    if (addressValidationResult.requiresConfirmation) {
      const confirmed = await lastValueFrom(
        this.dialog
          .open(WarningDialog, {
            width: '600px',
            data: {
              dialogType: 'addressLowConfidence',
              optionalData: {
                enteredAddress: saveObj.address,
                candidateAddress: [
                  addressValidationResult.street,
                  addressValidationResult.housenumber,
                  addressValidationResult.postal,
                  addressValidationResult.city,
                ]
                  .filter(Boolean)
                  .join(' '),
                coordinates: addressValidationResult.coordinates,
              },
            },
          })
          .afterClosed(),
      );

      if (!confirmed) {
        return false;
      }
    }
    const saveData = {
      ...saveObj,
      coordinates: addressValidationResult.coordinates,
      coordinatesFor: normalizedAddress,
    };
    await setDoc(docRef, saveData, { merge: true });
    saveObj.id = docRef.id;
    await this.logChanges(saveObj);
    return true;
  }

  async showWarningDialog(type): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = this.dialog.open(WarningDialog, {
        width: '350px',
        data: { dialogType: type },
      });
      dialogRef.afterClosed().subscribe((result) => {
        resolve(result);
      });
    });
  }

  async logChanges(newObj: any) {
    const changes = getObjectDifferences(this.data.item ?? {}, newObj, {
      ignoreKeys: ['id'],
    });
    if (!changes) {
      return;
    }

    let logType;
    switch (this.data.itemLabel) {
      case 'voertuig':
        logType = LogType.RidePreparationVehicle;
        break;
      case 'voertuiggroep':
        logType = LogType.RidePreparationVehicleGroup;
        break;
      case 'tarief':
        logType = LogType.RidePreparationRate;
        break;
      case 'kenmerk':
        logType = LogType.RidePreparationCharacteristic;
        break;
      case 'standaardlocatie':
        logType = LogType.RidePreparationStandardLocation;
        break;
      case 'telefoonnummer':
        logType = LogType.RidePreparationPhoneNumber;
        break;
    }
    if (!logType) {
      return;
    }

    const logObj: RidePreparationLog = {
      type: logType,
      date: new Date(),
      action: this.data.item ? 'update' : 'create',
      userEmail: dataStore.rootUser.email,
      userId: dataStore.rootUser.id,
      itemType: this.data.itemLabel,
      itemId: this.data?.item?.id ?? newObj.id,
      role:
        dataStore.rootUser.rights === 'admin'
          ? 'systemAdmin'
          : dataStore.orgUser?.roles?.join(', ') || 'onbekend',
      ...changes,
    };
    if (!this.data.item) {
      logObj.before = null;
    }

    await addDoc(
      collection(
        db,
        `eventLogs/${dataStore.selectedOrganisation.id}/changedRidePreparationSettings`,
      ),
      logObj,
    );
  }

  getError(control: string) {
    const field = this.form.get(control);
    if (field.touched || !field.pristine) {
      switch (true) {
        case field.hasError('required'):
          return 'Dit veld is verplicht.';

        case field.hasError('numberInput'):
          return 'Vul een geldige waarde in.';

        case field.hasError('phoneValidator'):
          return 'Telefoonnummer is ongeldig.';

        case field.hasError('orderValidator'):
          return 'Dit volgordenummer is al in gebruik.';

        case field.hasError('min'):
          return `De minimale waarde is ${field.getError('min').min}.`;
      }
      return '';
    }
  }
}
